import { Component, h, Host, Prop, State } from '@stencil/core';
import { UserType } from '../../../types/user-type';
import {
  AppService,
  DomService,
  GlobalConfig,
  GlobalContent,
  INSTITUTIONAL_CONSENT_PERSIST_QUERY_PARAM_KEY,
  INSTITUTIONAL_CONSENT_QUERY_PARAM_KEY,
  StringService,
} from '../../services';
import { Country, RcmSelectCustomEvent } from '../../components';
import { getTextWidthInPx, remCalc } from '../../style';
import { INSTITUTIONAL_CONSENT_EVENT, RETAIL_CONSENT_EVENT } from '../../constants';
import { Domain } from '../../../types/domain';

@Component({
  tag: 'rcm-user-dropdown-switcher',
  styleUrl: 'rcm-user-dropdown-switcher.scss',
  shadow: true,
})
export class RcmUserDropdownSwitcher {
  @Prop() userTypeOptions: UserType[];

  @State() dropdownMinWidth: string;

  @State() globalContent: GlobalContent;

  @State() globalConfig: GlobalConfig;

  @State() userTypeDisplayLabelMap: Record<UserType, string>;

  currentUserType = AppService.getUserType();

  currentDomain: Domain;

  currentCountry: Country;

  institutionalConsent: boolean = false;

  componentWillLoad() {
    this.globalContent = DomService.getGlobalContent();
    this.globalConfig = DomService.getGlobalConfig();
    this.currentDomain = this.globalConfig.domain as Domain;
    this.currentCountry = this.globalConfig.country;

    if (this.currentUserType === UserType.INSTITUTIONAL) {
      this.resolveInstitutionalConsent();
    }
    this.userTypeOptions = this.userTypeOptions || this.buildAvailableUserTypes();
    this.userTypeDisplayLabelMap = this.buildUserTypeDisplayLabelMap();
    this.dropdownMinWidth = this.calculateDropdownMinWidth(this.userTypeDisplayLabelMap);
    if (!AppService.isCurrentUserCountryConsentStored(this.currentUserType, this.currentCountry)) {
      this.showConsentModal(this.currentUserType);
    }
  }

  private buildAvailableUserTypes() {
    const { homepage } = this.globalContent.navigation;
    return Object.values(UserType).filter((userType) => !!homepage[userType] && !['/', ''].includes(homepage[userType]));
  }

  private resolveInstitutionalConsent() {
    // Read and process consent parameters
    this.institutionalConsent = DomService.getQueryParam(INSTITUTIONAL_CONSENT_QUERY_PARAM_KEY)?.toLowerCase() === 'true';
    const persistConsent = DomService.getQueryParam(INSTITUTIONAL_CONSENT_PERSIST_QUERY_PARAM_KEY)?.toLowerCase() === 'true';

    if (this.institutionalConsent) {
      AppService.setUserCountryConsent(UserType.INSTITUTIONAL, this.currentCountry, persistConsent);
    }

    let maskedUrl = DomService.removeQueryParam(window.location.href, INSTITUTIONAL_CONSENT_QUERY_PARAM_KEY);
    maskedUrl = DomService.removeQueryParam(maskedUrl, INSTITUTIONAL_CONSENT_PERSIST_QUERY_PARAM_KEY);
    window.history.replaceState({}, '', maskedUrl);
  }

  private buildUserTypeDisplayLabelMap(): Record<UserType, string> {
    const { userTypeSwitch } = this.globalContent.navigation;
    const tempUserTypeOptions = this.userTypeOptions.reduce(
      (acc, userType) => {
        const label = userTypeSwitch[userType];
        return label ? { ...acc, [userType]: label.trim() } : acc;
      },
      {} as Record<UserType, string>,
    );

    return tempUserTypeOptions;
  }

  private calculateDropdownMinWidth(labelMap: Record<UserType, string>): string {
    const BUFFER_SPACE_REM = 1.5;
    const longestLabel = Object.values(labelMap).reduce((a, b) => (b.length > a.length ? b : a), '');
    const textWidthInPx = getTextWidthInPx(longestLabel);
    return `${remCalc(textWidthInPx) + BUFFER_SPACE_REM}rem`;
  }

  private showConsentModal(userType: UserType) {
    const eventName = userType === UserType.RETAIL ? RETAIL_CONSENT_EVENT : INSTITUTIONAL_CONSENT_EVENT;
    window.dispatchEvent(new CustomEvent(eventName));
  }

  private switchUserType(selectEvent: RcmSelectCustomEvent<{ value: string }>) {
    const targetDisplayLabel = selectEvent.detail.value;

    const targetUserType = Object.keys(this.userTypeDisplayLabelMap).find((userType) => {
      return this.userTypeDisplayLabelMap[userType] === targetDisplayLabel;
    });

    // If the target is the same as current, handle consent if needed
    if (targetUserType === this.currentUserType) {
      if (!AppService.isCurrentUserCountryConsentStored(this.currentUserType, this.currentCountry)) {
        this.showConsentModal(this.currentUserType);
      }
      return;
    }

    // For institutional target, always show consent modal
    if (targetUserType === UserType.INSTITUTIONAL) {
      this.showConsentModal(targetUserType);
      return;
    }

    // For retail, wealth-management and consultant target, navigate directly to target homepage
    if (targetUserType === UserType.RETAIL || targetUserType === UserType.CONSULTANT || targetUserType === UserType.WEALTH_MANAGEMENT) {
      const userTypeSpecificHomepage = StringService.buildLocalisedHomePageLink({ userType: targetUserType });
      const normalizedUserTypeSpecificHomepage = StringService.normalizeLink(userTypeSpecificHomepage);
      DomService.visitSite(normalizedUserTypeSpecificHomepage);
    }
  }

  private getUserTypesLabelsString(): string {
    // Create an object mapping each UserType to its position in the enum
    const userTypesSortedInEnumOrder = Object.values(UserType).reduce((acc, type, index) => {
      acc[type] = index;
      return acc;
    }, {});

    const sortedLabels = this.userTypeOptions
      .sort((a, b) => {
        // Always put current user type first
        if (a === this.currentUserType) return -1;
        if (b === this.currentUserType) return 1;

        // Otherwise sort by enum order
        return userTypesSortedInEnumOrder[a] - userTypesSortedInEnumOrder[b];
      })
      .map((userType) => this.userTypeDisplayLabelMap[userType]);

    return sortedLabels.toString();
  }

  render() {
    return (
      <Host>
        <rcm-select
          noChangeOnLoad
          tempChoices={this.getUserTypesLabelsString()}
          name="language"
          small
          minWidth={this.dropdownMinWidth}
          onChangeEvent={(e) => this.switchUserType(e)}
        ></rcm-select>
      </Host>
    );
  }
}
